import { Injectable } from '@angular/core';
import { CelebrationEvent } from '../interfaces/celebration-event.interface';
import { CocoCelebrationEvent } from '../interfaces/coco-celebration-event.interface';
import { Celebration } from '../models/celebration.model';
import { CocoCelebrationAssets } from '../models/coco-celebration-assets.model';

@Injectable()
export class CocoCelebrationService {
  private events: CocoCelebrationEvent[] = [];

  constructor() {
    const today = new Date();
    const activeEvents = this.getEventsForDate(Celebration, today);
    this.events = this.mergeWithAssets(activeEvents);
  }

  /**
   * Returns the mascot path for the highest priority active celebration event.
   * Falls back to lower priority events if the highest priority event doesn't have a mascot.
   * @returns The mascot image path or null if no active celebration exists
   */
  public getMascot(): string {
    return this.getAssetByPriority(event => event.mascotPath);
  }

  /**
   * Returns the avatar path for the highest priority active celebration event.
   * Falls back to lower priority events if the highest priority event doesn't have an avatar.
   * @returns The avatar image path or null if no active celebration exists
   */
  public getAvatar(): string {
    return this.getAssetByPriority(event => event.avatarPath);
  }

  /**
   * Returns the animation configuration for the highest priority active celebration event.
   * Falls back to lower priority events if the highest priority event doesn't have an animation.
   * @returns The animation configuration object or null if no active celebration exists
   */
  public getAnimation(): CocoCelebrationEvent['globalAnimation'] | null {
    return this.getAssetByPriority(event => event.globalAnimation);
  }

  /**
   * Gets an asset from events ordered by priority.
   * Returns the asset from the first event that has a truthy value for the selector.
   */
  private getAssetByPriority<T>(selector: (event: CocoCelebrationEvent) => T): T | null {
    if (!this.events.length) return null;

    // Sort by priority (lowest number = highest priority)
    const sortedEvents = [...this.events].sort((a, b) => a.priority - b.priority);

    // Find the first event that has the requested asset
    for (const event of sortedEvents) {
      const asset = selector(event);
      if (asset !== null && asset !== undefined) {
        return asset;
      }
    }

    return null;
  }

  private getEventsForDate(events: CelebrationEvent[], date: Date): CelebrationEvent[] {
    const month = date.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
    const day = date.getDate();

    return events.filter(event => {
      // Check if current date falls within the event range
      // Handle events that span across year boundary (e.g., Dec 25 - Jan 5)
      if (event.startMonth === event.endMonth) {
        // Same month: simple check
        return month === event.startMonth && day >= event.startDay && day <= event.endDay;
      } else if (event.startMonth < event.endMonth) {
        // Event within same year (e.g., Oct 20 - Nov 5)
        return (
          (month === event.startMonth && day >= event.startDay) ||
          (month > event.startMonth && month < event.endMonth) ||
          (month === event.endMonth && day <= event.endDay)
        );
      }

      // Event spans year boundary (e.g., Dec 20 - Jan 10)
      return (
        (month === event.startMonth && day >= event.startDay) ||
        month > event.startMonth ||
        month < event.endMonth ||
        (month === event.endMonth && day <= event.endDay)
      );
    });
  }

  private mergeWithAssets(events: CelebrationEvent[]): CocoCelebrationEvent[] {
    return events
      .map(event => {
        const asset = CocoCelebrationAssets.find(a => a.type === event.type);
        if (!asset) {
          // No asset found, skip this event gracefully
          return null;
        }

        return {
          ...event,
          mascotPath: asset.mascotPath,
          avatarPath: asset.avatarPath,
          globalAnimation: asset.globalAnimation
        };
      })
      .filter((e): e is CocoCelebrationEvent => e !== null); // remove nulls
  }
}
